import { apiClient } from './client';
import type { StockItem, WarehouseMovement, NegativeStockAlert } from '../types/api.types';

// ─── Product types ────────────────────────────────────────────────────────────

export type ProductCategory = 'AGROQUIMICO' | 'FERTILIZANTE' | 'SEMILLA' | 'COMBUSTIBLE' | 'OTRO';
export type ProductUnit     = 'litros' | 'kg' | 'piezas' | 'toneladas' | 'cajas';

export interface Product {
  productId:         string;
  tenantId:          string;
  name:              string;
  category:          ProductCategory;
  unit:              ProductUnit;
  unitCost:          number;
  minStock:          number;
  description?:      string;
  activeIngredient?: string;
  registroSENASICA?: string;
  /** userIds del tenant a notificar por Telegram cuando el stock cruza minStock hacia abajo */
  notifyUserIds?:    string[];
  /** Roles del tenant a notificar (unión con notifyUserIds) */
  notifyRoles?:      string[];
  /** Solo lectura: URL firmada (1 h) de la miniatura, presente cuando hay imagen. */
  thumbnailUrl?:     string;
  createdAt:         string;
  updatedAt?:        string;
}

export interface ProductInput {
  name:              string;
  category:          ProductCategory;
  unit:              ProductUnit;
  unitCost:          number;
  minStock?:         number;
  description?:      string;
  activeIngredient?: string;
  registroSENASICA?: string;
  notifyUserIds?:    string[];
  notifyRoles?:      string[];
  ranchoId?:         string;
}

/** List all products for the tenant (optionally filter by category) */
export const fetchProducts = async (category?: ProductCategory, ranchoId?: string | null): Promise<Product[]> =>
  (await apiClient.get('/api/products', {
    params: category || ranchoId ? { ...(category && { category }), ...(ranchoId && { ranchoId }) } : undefined,
  })).data;

export const createProduct = async (input: ProductInput): Promise<Product> =>
  (await apiClient.post('/api/products', input)).data;

export const updateProduct = async (productId: string, input: Partial<ProductInput>): Promise<void> =>
  (await apiClient.put(`/api/products/${productId}`, input)).data;

export const deleteProduct = async (productId: string): Promise<void> =>
  (await apiClient.delete(`/api/products/${productId}`)).data;

/** Sube la miniatura de un producto: pide URL firmada, envía el archivo ya reducido y confirma. */
export const uploadProductImage = async (productId: string, blob: Blob): Promise<void> => {
  const contentType = blob.type || 'image/jpeg';
  const { uploadUrl, imageKey } = (await apiClient.post(
    `/api/products/${productId}/image-upload-url`,
    { contentType },
  )).data as { uploadUrl: string; imageKey: string };

  // PUT directo a S3 con URL firmada: sin el token de la API.
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob });
  if (!put.ok) throw new Error(`UPLOAD_FAILED_${put.status}`);

  await apiClient.post(`/api/products/${productId}/image-confirm`, { imageKey });
};

/** Current stock for a warehouse */
export const fetchStock = async (warehouseId: string): Promise<StockItem[]> =>
  (await apiClient.get(`/api/warehouse/${warehouseId}/stock`)).data;

/** Traspaso agrupado por transferId (GET /api/warehouses/transfers). */
export interface WarehouseTransferItem {
  transferId:           string;
  createdAt:            string;
  fromWarehouseId:      string;
  fromWarehouseName:    string;
  toWarehouseId:        string;
  toWarehouseName:      string;
  productId:            string;
  productName:          string;
  unit:                 string;
  qty:                  number;
  requestedBy:          string;
  reversedByTransferId: string | null;
  reversesTransferId:   string | null;
}

export interface WarehouseTransferPage {
  items:      WarehouseTransferItem[];
  nextCursor: string | null;
}

/** Listado de traspasos de la empresa, más reciente primero. */
export const fetchWarehouseTransfers = async (cursor?: string, limit = 20): Promise<WarehouseTransferPage> =>
  (await apiClient.get('/api/warehouses/transfers', { params: { limit, ...(cursor ? { cursor } : {}) } })).data;

/** Recent movements for a warehouse */
export const fetchMovements = async (
  warehouseId: string,
  limit = 50,
): Promise<WarehouseMovement[]> =>
  (await apiClient.get(`/api/warehouse/${warehouseId}/movements`, { params: { limit } })).data;

/** Movimientos de un producto en una bodega, con lote y equipo ya resueltos a nombre */
export const fetchProductMovements = async (
  warehouseId: string,
  productId: string,
  limit = 100,
): Promise<WarehouseMovement[]> =>
  (await apiClient.get(`/api/warehouses/${warehouseId}/products/${productId}/movements`, { params: { limit } })).data;

export interface MovementInput {
  productId:   string;
  type:        'IN' | 'OUT';
  qty:         number;
  reason:      string;
  unitCost?:   number;
  expiryDate?: string;
  loteNumber?: string;
  loteId?:     string;
}

/** Register a manual entry/exit movement for a warehouse */
export const createMovement = async (warehouseId: string, input: MovementInput): Promise<WarehouseMovement> =>
  (await apiClient.post(`/api/warehouse/${warehouseId}/movements`, input)).data;

export interface TransferInput {
  fromWarehouseId: string;
  toWarehouseId:   string;
  productId:       string;
  qty:             number;
  note?:           string;
}

export interface TransferResult {
  transferId: string;
  qty:        number;
  unitCost:   number;
  totalCost:  number;
  lines:      { qty: number; expiryDate?: string }[];
}

/** Traspaso de stock entre dos bodegas de la misma empresa (requiere warehouse:transfer) */
export const createTransfer = async (input: TransferInput): Promise<TransferResult> =>
  (await apiClient.post('/api/warehouses/transfers', input)).data;

/** All warehouses for the tenant */
export const fetchWarehouses = async (): Promise<{ warehouseId: string; name: string; ranchoId?: string | null }[]> =>
  (await apiClient.get('/api/warehouses')).data;

/** Warehouses filtered by the active ranch (only when `inventory.scopeByRancho` is on in the backend). */
export const fetchWarehousesByRancho = async (ranchoId?: string | null): Promise<{ warehouseId: string; name: string; ranchoId?: string | null }[]> =>
  (await apiClient.get('/api/warehouses', { params: ranchoId ? { ranchoId } : undefined })).data;

/** Low-stock items across all warehouses */
export const fetchLowStockAlerts = async (): Promise<StockItem[]> =>
  (await apiClient.get('/api/warehouse/alerts/low-stock')).data;

// ─── Negative stock alert queue ─────────────────────────────────────────────

/** OPEN negative-stock alerts for the tenant (cola de revisión de Inventario) */
export const fetchNegativeStockAlerts = async (): Promise<NegativeStockAlert[]> =>
  (await apiClient.get('/api/warehouse/negative-stock-alerts')).data.alerts;

export const ignoreAlert = async (warehouseId: string, productId: string, reason: string): Promise<void> =>
  (await apiClient.post(`/api/warehouse/negative-stock-alerts/${warehouseId}/${productId}/ignore`, { reason })).data;

export const ignoreAllAlerts = async (
  warehouseId: string,
  reason: string,
): Promise<{ ignoredCount: number; scope: 'WAREHOUSE' | 'TENANT' }> =>
  (await apiClient.post('/api/warehouse/negative-stock-alerts/ignore-all', { warehouseId, reason })).data;

// ─── Movement edit/delete (diagnóstico de stock negativo) ──────────────────

export interface MovementPatch {
  qty?:  number;
  type?: 'IN' | 'OUT';
}

export const updateMovement = async (
  warehouseId: string,
  movementId: string,
  patch: MovementPatch,
): Promise<WarehouseMovement> =>
  (await apiClient.put(`/api/warehouse/${warehouseId}/movements/${movementId}`, patch)).data;

export const deleteMovement = async (warehouseId: string, movementId: string): Promise<void> =>
  (await apiClient.delete(`/api/warehouse/${warehouseId}/movements/${movementId}`)).data;
